import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, idr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { DatePicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { formatTanggal, hariIniISO } from "../../lib/tanggal";
import InputRupiah from "../../components/InputRupiah";
import AttachmentPanel from "./AttachmentPanel";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";
import { BatasDaftarNotice } from "./_shared";

// `_get_items` (backend cashback_claim.py) mengurut imei asc lalu memotong
// `to_list(5000)` per batch — nyaris mustahil kesentuh untuk satu Claim
// Batch, tapi tetap didampingi `BatasDaftarNotice` demi konsisten.
const CB_ITEMS_BATAS = 5000;
import {
  ArrowLeft, Smartphone, Send, CheckCircle2, Ban, Wallet, RefreshCcw,
  Paperclip, History, PlusCircle, Trash2, AlertTriangle, ExternalLink, Search, Info, Pencil,
} from "lucide-react";
import {
  CB_BASE, Modal, Field, inputCls, ReasonModal, ActionBtn,
  ClaimStatusPill, ItemStatusPill, DeadlineBadge, PeriodeKlaimBadge,
} from "./CashbackProgram";
import ImporHasilSupplier from "./pp/ImporHasilSupplier";
import SelKolom, { kelasRata, rataKolom } from "./pp/SelKolom";
import { KOLOM_ITEM, kunciJenis, aksesUrut } from "./pp/halamanPP";

// Status tempat settlement boleh dicatat — SAMA PERSIS dengan
// `BATCH_STATUS_BOLEH_SETTLEMENT` di backend (`cashback_claim.py`). Murni
// gerbang tampilan; backend tetap menolak sendiri kalau ada yang lolos.
const STATUS_BOLEH_SETTLEMENT = ["diputuskan", "settlement_sebagian"];
// Batch yang keputusan 'approved'-nya boleh dikoreksi — SAMA PERSIS dengan
// `BATCH_STATUS_BOLEH_KOREKSI` di backend (`cashback_claim.py`).
const STATUS_BOLEH_KOREKSI = ["diputuskan", "settlement_sebagian"];
const STATUS_FINAL = ["settled", "closed", "dibatalkan"];
// IMEI baru hanya boleh masuk batch yang BELUM diputuskan — SAMA PERSIS dengan
// `BATCH_STATUS_BOLEH_TAMBAH_IMEI` di backend (dijaga tes
// `test_ppuang_gerbang_layar.py`). Batch 'diputuskan' tidak bisa disubmit lagi,
// jadi IMEI yang ditambahkan ke sana jadi draft abadi yang mengunci IMEI-nya.
const BATCH_STATUS_BOLEH_TAMBAH_IMEI = ["draft", "submitted"];
// Role yang boleh mengedit daftar IMEI batch SETELAH disubmit — SAMA PERSIS
// dengan `_pastikan_boleh_edit_item_batch` di backend.
const ROLE_EDIT_AFTER_SUBMIT = ["finance_spv", "finance_ceo", "admin", "superadmin"];

export default function CashbackClaimDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, can } = useAuth();

  const [claim, setClaim] = useState(null);
  const [items, setItems] = useState([]);
  const [statusCn, setStatusCn] = useState({});
  // Keputusan backend "uang settlement batch ini DITAHAN" (pertanyaan Bos no. 21,
  // uang Dealer Partner). Dibaca apa adanya dari `detail_claim` — fungsi yang
  // SAMA dengan pra-validasi settlement, jadi layar tidak menghitung sendiri
  // (kalau menghitung sendiri, tombol bisa tampil lalu berujung 409).
  const [penahanan, setPenahanan] = useState(null);
  // PP4: program batch ini Selesai / menunggu keputusan PIC → tambah IMEI &
  // reclaim ditolak backend 409. Keputusannya dibaca dari backend.
  const [programTerkunci, setProgramTerkunci] = useState(null);
  // Tinjauan PP r1: program batch ini SELESAI → uangnya ikut dibekukan
  // (settlement/void/batal ditolak backend 409). Keputusannya dari backend.
  const [uangTerkunci, setUangTerkunci] = useState(null);
  // Kategori CN bawaan batch ("Support Aktivasi" untuk batch program aktivasi
  // kalau kategori itu aktif) — keputusan backend, layar hanya memilihnya.
  const [kategoriBawaan, setKategoriBawaan] = useState(null);
  // Kolom tabel IMEI per jenis program (PP2) — `jenis_tampilan` dari backend,
  // layar tidak menafsirkan `mode_mesin`/`jenis_program` batch sendiri.
  const [jenisTampilan, setJenisTampilan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reload, setReload] = useState(0);
  const bump = () => setReload((n) => n + 1);
  const [tab, setTab] = useState("overview");
  const [modalKey, setModalKey] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [showAtt, setShowAtt] = useState(false);
  const [busy, setBusy] = useState(false);

  const canUpdate = can("finance_cashback_claim", "update");
  const canDelete = can("finance_cashback_claim", "delete");

  useEffect(() => {
    let batal = false;
    setLoading(true); setLoadError(null);
    (async () => {
      try {
        const r = await api.get(`${CB_BASE}/claims/${id}`);
        if (!batal) {
          setClaim(r.data.item); setItems(r.data.items || []); setStatusCn(r.data.status_cn || {});
          setPenahanan(r.data.settlement_ditahan || null);
          setProgramTerkunci(r.data.program_terkunci_alasan || null);
          setUangTerkunci(r.data.uang_terkunci_alasan || null);
          setKategoriBawaan(r.data.kategori_cn_bawaan || null);
          setJenisTampilan(r.data.jenis_tampilan || null);
        }
      } catch (e) {
        if (!batal) { setLoadError(e); toast.error(e?.response?.data?.detail || "Gagal memuat Claim Batch"); }
      } finally { if (!batal) setLoading(false); }
    })();
    return () => { batal = true; };
  }, [id, reload]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(r?.data?.message || okMsg);
      bump();
      return r;
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memproses"); return null; }
    finally { setBusy(false); }
  };

  const doBatalkan = (reason) => act(() => api.post(`${CB_BASE}/claims/${id}/batalkan`, { reason }), "Claim Batch dibatalkan ✓");
  const doVoidSettlement = (reason) => act(() => api.post(`${CB_BASE}/claims/${id}/settlement/${voidTarget.id}/void`, { reason }), "Settlement di-void ✓");

  if (loading || loadError || !claim) {
    return (
      <div className="p-4 md:p-8">
        <LoadingState loading={loading} error={loadError} onRetry={bump} varian="kartu" jumlahKartu={3} data-testid="cb-claimdet-loading" />
      </div>
    );
  }

  const outstanding = Math.max(0, round2((claim.total_disetujui || 0) - (claim.nominal_settled || 0)));
  const adaItemSubmitted = items.some((i) => i.status === "submitted");
  const rejectedBisaReclaim = items.filter((i) => i.status === "rejected" && !i.reclaimed_in_item_id);
  // "Kelola IMEI" punya dua mode: TAMBAH (hanya batch draft/submitted — backend
  // menolak selain itu) dan KELUARKAN (item draft/submitted). Batch yang sudah
  // diputuskan tapi masih memegang item draft lama tetap bisa membuka modal
  // untuk mengeluarkannya — satu-satunya jalan melepas IMEI yang terkunci.
  const bolehTambahImei = BATCH_STATUS_BOLEH_TAMBAH_IMEI.includes(claim.status);
  const adaItemBisaDikeluarkan = items.some((i) => i.klaim_aktif && ["draft", "submitted"].includes(i.status));
  const bolehEditItems = !STATUS_FINAL.includes(claim.status) && canUpdate &&
    (claim.status === "draft" || ROLE_EDIT_AFTER_SUBMIT.includes(user?.role)) &&
    (bolehTambahImei || adaItemBisaDikeluarkan);
  // Void yang tertahan setengah jalan (`void_belum_tuntas`, backend PP-UANG):
  // nominalnya sudah kembali outstanding tapi uang settlement lamanya mungkin
  // masih hidup. Backend MENOLAK settlement baru & pembatalan batch (409)
  // sampai entri itu dituntaskan — tombolnya disembunyikan di sini dan diganti
  // pemberitahuan yang menunjuk ke tombol "Tuntaskan".
  const adaVoidTertahan = (claim.settlements || []).some((s) => s.void_belum_tuntas);
  const uangDitahan = Boolean(penahanan && penahanan.ditahan);
  // Keputusan Bos no. 21 (25 Sep 2026): uang HP Dealer Partner milik ZOA (4910).
  // Batch yang memuatnya menampilkan aturan itu di dekat tombol settlement —
  // teksnya dari backend (`penahanan.catatan`), tidak diketik ulang di layar.
  const uangMilikZoa = Boolean(penahanan && penahanan.dealer && !penahanan.ditahan);
  const bolehBatalkan = !STATUS_FINAL.includes(claim.status) && canDelete && (claim.nominal_settled || 0) <= 0.5 &&
    !adaVoidTertahan && !uangTerkunci;
  // Koreksi Keputusan (tinjauan PP r2): jalan resmi batch yang dibayar supplier
  // KURANG dari yang disetujui. Endpoint digerbangi `finance_cashback_claim.delete`
  // — kunci yang SAMA dengan Void/Batalkan — dan menolak void tertahan & program
  // Selesai, jadi tombolnya ikut disembunyikan untuk keadaan itu.
  const bolehKoreksi = STATUS_BOLEH_KOREKSI.includes(claim.status) && canDelete && !adaVoidTertahan && !uangTerkunci &&
    items.some((i) => i.status === "approved");

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-cashback-claim-detail">
      <button onClick={() => navigate("/finance/cashback/claims")} data-testid="cb-claimdet-back"
              className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1.5">
        <ArrowLeft className="w-4 h-4" /> Kembali ke Daftar Cashback Claim
      </button>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <div className="flex items-start justify-between flex-col md:flex-row gap-3">
          <div>
            <p className="font-mono text-xs text-slate-500">{claim.code}</p>
            <h1 className="font-display text-xl md:text-2xl font-bold text-slate-900 mt-1 flex items-center gap-2">
              <Smartphone className="w-6 h-6 text-emerald-600" /> {claim.supplier_nama}
            </h1>
            <p className="text-sm text-slate-600 mt-1">{claim.program_nama} ({claim.program_code}){claim.brand ? ` · ${claim.brand}` : ""}{claim.cabang ? ` · ${claim.cabang}` : ""}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <ClaimStatusPill status={claim.status} />
            <DeadlineBadge deadline={claim.deadline_submit_saat_dibuat} statusBatch={claim.status} submittedAfterDeadline={claim.submitted_after_deadline} />
            <PeriodeKlaimBadge diLuarPeriodeKlaim={claim.di_luar_periode_klaim} alasan={claim.di_luar_periode_klaim_alasan} />
          </div>
        </div>

        {adaVoidTertahan && (
          <div className="mt-4 bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="cb-claimdet-void-tertahan-info">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <p>
              Ada void settlement yang <b>tertahan setengah jalan</b> — pembalikan uangnya belum bisa dipastikan.
              Settlement baru dan pembatalan batch ditahan supaya cashback yang sama tidak tercatat dua kali.
              {canDelete
                ? " Buka tab Settlement lalu tekan \"Tuntaskan\" pada entri bertanda \"Void belum tuntas\"."
                : " Minta Finance SPV (izin hapus Cashback Claim) menuntaskannya di tab Settlement."}
            </p>
          </div>
        )}

        {uangDitahan && STATUS_BOLEH_SETTLEMENT.includes(claim.status) && (
          <div className="mt-4 bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="cb-claimdet-dealer-ditahan-info">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <p><b>Uang ditahan — Pengaturan Price Protection.</b> {penahanan.alasan}</p>
          </div>
        )}

        {uangMilikZoa && STATUS_BOLEH_SETTLEMENT.includes(claim.status) && (
          <div className="mt-4 bg-sky-50 border border-sky-200 rounded-lg p-3 text-sm text-sky-900 flex items-start gap-2" data-testid="cb-claimdet-dealer-milik-zoa-info">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <p>{penahanan.catatan}</p>
          </div>
        )}

        {uangTerkunci && (
          <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-900 flex items-start gap-2" data-testid="cb-claimdet-uang-terkunci-info">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <p>{uangTerkunci}</p>
          </div>
        )}

        {claim.status === "dibatalkan" && (
          <div className="mt-4 bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-800" data-testid="cb-claimdet-cancel-info">
            <p className="font-semibold">Dibatalkan oleh {claim.batal_by} pada {claim.batal_at ? formatTanggal(claim.batal_at, { denganJam: true }) : "-"}</p>
            <p className="mt-1">{claim.batal_reason}</p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
          <StatBox label="Jumlah IMEI" value={claim.jumlah_item} />
          <StatBox label="Total Klaim" value={idr(claim.total_klaim)} />
          <StatBox label="Disetujui" value={idr(claim.total_disetujui)} tone="text-emerald-700" />
          <StatBox label="Ditolak" value={idr(claim.total_ditolak)} tone="text-rose-700" />
          <StatBox label="Settled" value={idr(claim.nominal_settled)} tone="text-emerald-700" />
          <StatBox label="Outstanding" value={idr(outstanding)} tone="text-slate-900" big />
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-100">
          {claim.status === "draft" && canUpdate && (
            <ActionBtn icon={Send} label="Submit ke Supplier" onClick={() => setModalKey("submit")} testid="cb-claimdet-act-submit" primary disabled={busy} />
          )}
          {bolehEditItems && (
            <ActionBtn icon={PlusCircle} label="Kelola IMEI" onClick={() => setModalKey("items")} testid="cb-claimdet-act-items" disabled={busy} />
          )}
          {adaItemSubmitted && canUpdate && (
            <ActionBtn icon={CheckCircle2} label="Putuskan Approve/Reject" onClick={() => setModalKey("putuskan")} testid="cb-claimdet-act-putuskan" primary disabled={busy} />
          )}
          {STATUS_BOLEH_SETTLEMENT.includes(claim.status) && outstanding > 0.5 && canUpdate && !adaVoidTertahan &&
            !uangDitahan && !uangTerkunci && (
            <ActionBtn icon={Wallet} label="Catat Settlement" onClick={() => setModalKey("settlement")} testid="cb-claimdet-act-settlement" primary disabled={busy} />
          )}
          {bolehKoreksi && (
            <ActionBtn icon={Pencil} label="Koreksi Keputusan" onClick={() => setModalKey("koreksi")} testid="cb-claimdet-act-koreksi" disabled={busy} />
          )}
          {rejectedBisaReclaim.length > 0 && canUpdate && !programTerkunci && (
            <ActionBtn icon={RefreshCcw} label={`Reclaim ${rejectedBisaReclaim.length} IMEI Ditolak`} onClick={() => setModalKey("reclaim")} testid="cb-claimdet-act-reclaim" disabled={busy} />
          )}
          {bolehBatalkan && (
            <ActionBtn icon={Ban} label="Batalkan Batch" onClick={() => setModalKey("batalkan")} testid="cb-claimdet-act-batalkan" tone="rose" disabled={busy} />
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="border-b border-slate-100 flex items-center gap-1 px-2 overflow-x-auto">
          {[
            { key: "overview", label: "Overview" },
            { key: "items", label: `IMEI (${items.length})` },
            { key: "settlement", label: "Settlement" },
            { key: "lampiran", label: "Lampiran" },
            { key: "activity", label: "Activity Log" },
          ].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`cb-claimdet-tab-${t.key}`}
                    className={`px-4 h-11 text-sm font-medium border-b-2 whitespace-nowrap transition ${tab === t.key ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === "overview" && (
            <div className="space-y-4">
              {claim.submitted_after_deadline && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 inline-flex items-start gap-2" data-testid="cb-claimdet-warn-deadline">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Batch ini disubmit SETELAH deadline program ({formatTanggal(claim.deadline_submit_saat_dibuat)}) — supplier berhak menolak klaim ini karena lewat batas waktu.
                </div>
              )}
              {claim.di_luar_periode_klaim && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 inline-flex items-start gap-2" data-testid="cb-claimdet-warn-periode-klaim">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Batch ini dibuat/ditambah DI LUAR periode klaim program — {claim.di_luar_periode_klaim_alasan || "periode klaim tidak cocok"}. Klaim tetap tersimpan, tapi cek dulu apakah supplier masih mau memprosesnya.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoRow label="Program" value={`${claim.program_code} · ${claim.program_nama}`} />
                <InfoRow label="Supplier" value={claim.supplier_nama} />
                <InfoRow label="Tanggal Submit" value={claim.tanggal_submit ? formatTanggal(claim.tanggal_submit) : "Belum disubmit"} />
                <InfoRow label="Deadline Submit Program" value={formatTanggal(claim.deadline_submit_saat_dibuat)} />
                <InfoRow label="Dibuat oleh" value={`${claim.created_by_name || claim.created_by || "-"} · ${formatTanggal(claim.created_at, { denganJam: true })}`} />
                {claim.keterangan && <InfoRow label="Keterangan" value={claim.keterangan} col2 />}
              </div>
            </div>
          )}

          {tab === "items" && <ItemsTab items={items} jenisTampilan={jenisTampilan} />}

          {tab === "settlement" && (
            <SettlementTab claim={claim} statusCn={statusCn} canDelete={canDelete} uangTerkunci={uangTerkunci}
                           canViewCN={can("finance_credit_note", "view")}
                           onVoid={(s) => setVoidTarget(s)} navigate={navigate} />
          )}

          {tab === "lampiran" && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Lampirkan bukti kirim ke supplier, konfirmasi approval, atau bukti settlement.</p>
              <button onClick={() => setShowAtt(true)} data-testid="cb-claimdet-open-attachments"
                      className="h-10 px-4 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-medium inline-flex items-center gap-2">
                <Paperclip className="w-4 h-4" /> Kelola Dokumen Lampiran
              </button>
            </div>
          )}

          {tab === "activity" && (
            <div className="space-y-2" data-testid="cb-claimdet-activity-log">
              {[...(claim.riwayat_status || [])].sort((a, b) => (b.at || "").localeCompare(a.at || "")).map((r, i) => (
                <div key={i} data-testid={`cb-claimdet-log-row-${i}`} className="flex items-start gap-3 border-b border-slate-50 pb-2 last:border-0">
                  <History className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ClaimStatusPill status={r.status} />
                      <span className="text-xs text-slate-500">{formatTanggal(r.at, { denganJam: true })}</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">oleh {r.by}{r.catatan ? ` — ${r.catatan}` : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showAtt && (
        <AttachmentPanel module="cashback_claim" objId={claim.id} objLabel={`${claim.code} · ${claim.supplier_nama}`}
                          onClose={() => { setShowAtt(false); bump(); }} />
      )}

      {modalKey === "submit" && (
        <SubmitModal claim={claim} onClose={() => setModalKey(null)} onDone={() => { setModalKey(null); bump(); }} />
      )}
      {modalKey === "items" && (
        <KelolaImeiModal claim={claim} items={items} bolehTambah={bolehTambahImei} programTerkunci={programTerkunci}
                         onClose={() => setModalKey(null)} onDone={() => { setModalKey(null); bump(); }} />
      )}
      {modalKey === "putuskan" && (
        <PutuskanModal claim={claim} items={items.filter((i) => i.status === "submitted")}
                       onClose={() => setModalKey(null)} onDone={() => { setModalKey(null); bump(); }} />
      )}
      {modalKey === "settlement" && (
        <SettlementModal claim={claim} outstanding={outstanding} kategoriBawaan={kategoriBawaan}
                         onClose={() => setModalKey(null)} onDone={() => { setModalKey(null); bump(); }} />
      )}
      {modalKey === "koreksi" && (
        <KoreksiKeputusanModal claim={claim} items={items.filter((i) => i.status === "approved")}
                               onClose={() => setModalKey(null)} onDone={() => { setModalKey(null); bump(); }} />
      )}
      {modalKey === "reclaim" && (
        <ReclaimModal claim={claim} items={rejectedBisaReclaim} onClose={() => setModalKey(null)}
                     onDone={(newBatch) => { setModalKey(null); if (newBatch) navigate(`/finance/cashback/claims/${newBatch.id}`); else bump(); }} />
      )}
      {modalKey === "batalkan" && (
        <ReasonModal title="Batalkan Claim Batch" icon={Ban} label="Alasan pembatalan" submitLabel="Batalkan Batch"
                     testid="cb-claimdet-batalkan-modal" onClose={() => setModalKey(null)}
                     onSubmit={async (reason) => { const r = await doBatalkan(reason); if (r) setModalKey(null); }} />
      )}
      {voidTarget && (
        // "Tuntaskan" memakai endpoint void yang sama (backend membedakan dari
        // penanda `void_belum_tuntas`) — hanya judul & labelnya yang berbeda.
        <ReasonModal title={voidTarget.void_belum_tuntas ? "Tuntaskan Void Settlement" : "Void Settlement"} icon={Ban}
                     label={voidTarget.void_belum_tuntas ? "Catatan penuntasan" : "Alasan void"}
                     submitLabel={voidTarget.void_belum_tuntas ? "Tuntaskan" : "Void Settlement"}
                     testid="cb-claimdet-void-modal" onClose={() => setVoidTarget(null)}
                     onSubmit={async (reason) => { const r = await doVoidSettlement(reason); if (r) setVoidTarget(null); }} />
      )}
    </div>
  );
}

function round2(n) { return Math.round((n + Number.EPSILON) * 100) / 100; }

// ============ Sub-komponen header ============
function StatBox({ label, value, tone, big }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-display font-bold mt-0.5 ${big ? "text-xl" : "text-lg"} ${tone || "text-slate-800"}`}>{value}</p>
    </div>
  );
}

function InfoRow({ label, value, col2 }) {
  return (
    <div className={col2 ? "md:col-span-2" : ""}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-sm text-slate-800 mt-0.5">{value}</p>
    </div>
  );
}

// ============ Tab: IMEI (brief butir 9 & 15 — item DITOLAK tetap terlihat,
// TIDAK PERNAH dihapus dari layar, lengkap dengan alasan eligibility saat
// diklaim supaya bisa diaudit). ============
// Diekspor supaya tes merender tabelnya dengan respons backend sungguhan.
export function ItemsTab({ items, jenisTampilan }) {
  // Kolom per jenis dari SATU peta (`pp/halamanPP.js::KOLOM_ITEM`): "Modal
  // (DIHAPUS)" untuk program jenis baru (brief hal. 4 & 7), SN & nominal per
  // jenis; batch program LAMA tetap kolom hari ini (Modal Asli, Cashback Klaim).
  const kolom = KOLOM_ITEM[kunciJenis(jenisTampilan)];
  // Urutan bawaan (brief butir 9 & 15): item DITOLAK tampil paling atas
  // supaya tidak tenggelam saat diaudit, baru diurut IMEI. `sortKey` null =
  // urutan ini yang tampil; klik header lain mengganti urutan (poin
  // pengurutan Finance 21 Agu 2026) SAMPAI diklik lagi untuk kembali null.
  const defaultSorted = [...items].sort((a, b) => (a.status === "rejected" ? -1 : 0) - (b.status === "rejected" ? -1 : 0) || a.imei.localeCompare(b.imei));
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sorted = sortRows(defaultSorted, sortKey, sortDir, aksesUrut(kolom));
  return (
    <div className="space-y-2">
      <BatasDaftarNotice shown={items.length} batas={CB_ITEMS_BATAS} />
      <div className="border border-slate-200 rounded-lg overflow-x-auto" data-testid="cb-claimdet-items-tabel" data-kolom={kunciJenis(jenisTampilan)}>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
          <tr>
            {kolom.map((c) => (c.urut ? (
              <SortTh key={c.kunci} label={c.label} sortKeyName={c.kunci} sortKey={sortKey} sortDir={sortDir} onSort={toggle}
                      align={rataKolom(c) === "right" ? "right" : "left"} testid={`cb-claimdet-sort-${c.tid}`} />
            ) : (
              <th key={c.kunci} className={`px-3 py-2 ${kelasRata(c)} ${c.format === "catatan_item" ? "min-w-[200px]" : ""}`}>{c.label}</th>
            )))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {sorted.map((it) => (
            <tr key={it.id} data-testid={`cb-claimdet-item-row-${it.imei}`} className={it.status === "rejected" ? "bg-rose-50/40" : ""}>
              {kolom.map((c) => (
                <td key={c.kunci} className={`px-3 py-2 ${kelasRata(c)}`}><SelKolom kolom={c} baris={it} Status={ItemStatusPill} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ============ Tab: Settlement (brief butir 17-18) ============
// Label jalur void — dari `voided_via` backend (PP-UANG). Entri lama tanpa medan
// ini tetap tampil "Voided" seperti dulu.
const VOID_VIA_LABEL = {
  void_cashback: "di-void dari sini",
  batal_credit_note: "CN-nya dibatalkan di menu Credit Note",
  selesaikan_cn_mati: "diselesaikan — CN-nya sudah tidak berlaku",
};

function SettlementTab({ claim, statusCn, canDelete, uangTerkunci, canViewCN, onVoid, navigate }) {
  // Urutan bawaan: settlement terbaru dulu. `settlements` adalah array
  // NESTED di dokumen klaim (bukan hasil query terpotong) — aman disortir
  // tanpa BatasDaftarNotice.
  const defaultRows = [...(claim.settlements || [])].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  const { sortKey, sortDir, toggle } = useSortState(null);
  const rows = sortRows(defaultRows, sortKey, sortDir, {
    tanggal: (s) => s.tanggal || s.at || "",
    metode: (s) => s.metode || "",
    nominal: (s) => s.nominal || 0,
    oleh: (s) => s.by || "",
  });
  if (rows.length === 0) return <p className="text-sm text-slate-400 py-8 text-center">Belum ada settlement tercatat.</p>;
  return (
    <div className="border border-slate-200 rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
          <tr>
            <SortTh label="Tanggal" sortKeyName="tanggal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-settle-sort-tanggal" />
            <SortTh label="Metode" sortKeyName="metode" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-settle-sort-metode" />
            <SortTh label="Nominal" sortKeyName="nominal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="cb-settle-sort-nominal" />
            <th className="text-left px-3 py-2">Keterangan</th>
            <SortTh label="Oleh" sortKeyName="oleh" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-settle-sort-oleh" />
            <th className="text-center px-3 py-2">Status</th>
            <th className="text-right px-3 py-2">Aksi</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((s) => (
            <tr key={s.id} data-testid={`cb-settle-row-${s.id}`} className={s.voided ? "opacity-50" : ""}>
              <td className="px-3 py-2">{formatTanggal(s.tanggal)}</td>
              <td className="px-3 py-2">
                {METODE_LABEL_LOCAL[s.metode] || s.metode}
                {(s.credit_note_code || statusCn[s.credit_note_id]?.code) && (
                  <p className="text-[10px] font-mono text-slate-500">{s.credit_note_code || statusCn[s.credit_note_id]?.code}</p>
                )}
              </td>
              <td className="px-3 py-2 text-right font-mono">{idr(s.nominal)}</td>
              <td className="px-3 py-2 text-xs text-slate-600">
                {s.keterangan || "-"}
                {s.voided && (s.voided_reason || s.voided_via) && (
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Void: {VOID_VIA_LABEL[s.voided_via] || "di-void"}{s.voided_reason ? ` — ${s.voided_reason}` : ""}
                  </p>
                )}
              </td>
              <td className="px-3 py-2 text-xs">{s.by_name || s.by}</td>
              <td className="px-3 py-2 text-center">
                {s.voided
                  ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Voided</span>
                  : <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">Aktif</span>}
                {!s.voided && s.perlu_pemeriksaan && (
                  <span data-testid={`cb-settle-perlu-periksa-${s.id}`}
                        className="block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                        title="Settlement ini tertahan setengah jalan — jangan diulang.">
                    Perlu pemeriksaan
                  </span>
                )}
                {/* Arahan jalan keluar TERLIHAT (bukan title/hover — tidak ada di
                    tablet/HP), dan beda per metode: dulu semua metode disuruh
                    "selesaikan lewat Credit Note", padahal kas/bank & potong utang
                    tidak punya CN — jalan keluarnya Void di baris ini (backend
                    membalik hanya uang yang benar-benar tercatat). */}
                {!s.voided && s.perlu_pemeriksaan && s.metode === "credit_note" && (
                  <span data-testid={`cb-settle-perlu-periksa-cn-${s.id}`} className="block mt-0.5 text-[10px] text-amber-800">
                    Jangan diulang — batalkan CN-nya di menu Credit Note (CN sudah tidak berlaku: Void)
                  </span>
                )}
                {!s.voided && s.perlu_pemeriksaan && s.metode !== "credit_note" && (
                  <span data-testid={`cb-settle-perlu-periksa-void-${s.id}`} className="block mt-0.5 text-[10px] text-amber-800">
                    Jangan diulang — bereskan dengan Void (izin hapus Cashback Claim)
                  </span>
                )}
                {/* Entri yang SUDAH ter-void pun bisa perlu pemeriksaan: void-nya
                    tertahan (uang lama mungkin masih hidup) dan batch ditahan
                    sampai dituntaskan. Dulu lencana hanya untuk entri aktif. */}
                {s.voided && s.void_belum_tuntas && (
                  <span data-testid={`cb-settle-void-tertahan-${s.id}`}
                        className="block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                        title="Void ini berhenti setengah jalan — pembalikan uangnya belum dipastikan. Tekan Tuntaskan.">
                    Void belum tuntas
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-right">
                <AksiSettlement s={s} infoCn={statusCn[s.credit_note_id]} canDelete={canDelete} canViewCN={canViewCN}
                                uangTerkunci={uangTerkunci} onVoid={onVoid} navigate={navigate} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const METODE_LABEL_LOCAL = {
  credit_note: "Credit Note (CN)",
  kas_bank: "Kas/Bank Langsung",
  potong_utang_langsung: "Potong Utang Langsung",
};

// Aksi per baris settlement. Metode credit_note punya dua keadaan:
// - CN masih berjalan → pembatalannya HANYA di menu Credit Note (backend
//   menolak void dari sini). Tautan hanya aktif bagi pemegang izin lihat CN;
//   selain itu teks yang menyebut izin yang dibutuhkan (bukan tautan ke 403).
// - CN sudah mati (dibatalkan/terhapus/tidak ada) → Void di sini menyelesaikan
//   entrinya (gerbang backend = hapus Cashback Claim, sama dengan `canDelete`).
//   Entri tanpa pointer CN diperlakukan sama seperti backend (`cn_id` kosong =
//   tidak ada CN yang bisa dibatalkan di menu CN); backend tetap menolak kalau
//   ternyata masih ada CN hidup dari batch ini bernilai sama.
function AksiSettlement({ s, infoCn, canDelete, canViewCN, uangTerkunci, onVoid, navigate }) {
  // Entri ter-void yang void-nya tertahan (`void_belum_tuntas`): "Tuntaskan"
  // memakai endpoint & izin yang SAMA dengan Void (hapus Cashback Claim). Untuk
  // metode CN yang CN-nya masih hidup, backend menolak (409) — CN dibatalkan
  // dulu di menu Credit Note, jadi yang tampil adalah arahan, bukan tombol.
  if (s.voided) {
    if (!s.void_belum_tuntas || !canDelete) return null;
    if (s.metode === "credit_note" && s.credit_note_id && infoCn && !infoCn.mati) {
      return (
        <span className="text-[10px] text-amber-800" data-testid={`cb-settle-tuntaskan-cn-hidup-${s.id}`}>
          Batalkan CN {infoCn.code || ""} di menu Credit Note dulu, lalu Tuntaskan di sini
        </span>
      );
    }
    return (
      <button onClick={() => onVoid(s)} data-testid={`cb-settle-tuntaskan-${s.id}`}
              className="text-amber-700 hover:text-amber-900 text-xs font-medium">
        Tuntaskan void
      </button>
    );
  }
  const cnMati = !s.credit_note_id || !!infoCn?.mati;
  if (s.metode === "credit_note" && !cnMati) {
    if (!canViewCN) {
      return (
        <span className="text-[10px] text-slate-500" data-testid={`cb-settle-cn-teks-${s.id}`}>
          Minta Finance SPV membatalkan CN ini di menu Credit Note (butuh izin hapus Credit Note + hapus Cashback Claim)
        </span>
      );
    }
    return (
      <button onClick={() => navigate(s.credit_note_id ? `/finance/credit-note/${s.credit_note_id}` : "/finance/credit-note")}
              data-testid={`cb-settle-cn-link-${s.id}`} className="text-sky-700 hover:text-sky-900 text-xs font-medium inline-flex items-center gap-1">
        Kelola di Credit Note <ExternalLink className="w-3 h-3" />
      </button>
    );
  }
  // Program Selesai: Void ditolak backend 409 (tinjauan PP r1) — "Tuntaskan"
  // di atas tetap ada karena ia menuntaskan pembalikan yang sudah terjadi.
  if (!canDelete || uangTerkunci) return null;
  return (
    <button onClick={() => onVoid(s)} data-testid={`cb-settle-void-${s.id}`} className="text-rose-600 hover:text-rose-800 text-xs font-medium">
      {s.metode === "credit_note" ? "Void (CN sudah tidak berlaku)" : "Void"}
    </button>
  );
}

// ============ Modal: Submit ke Supplier ============
function SubmitModal({ claim, onClose, onDone }) {
  const [f, setF] = useState({ tanggal_submit: hariIniISO(), catatan: "" });
  const [busy, setBusy] = useState(false);
  const lewatDeadline = f.tanggal_submit > claim.deadline_submit_saat_dibuat;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${CB_BASE}/claims/${claim.id}/submit`, f);
      toast.success(r.data.message); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal submit Claim Batch"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Submit ke Supplier" icon={Send} onClose={onClose} onSubmit={submit} submitLabel="Submit Claim Batch" busy={busy} testid="cb-claimdet-submit-modal">
      <p className="text-sm text-slate-600">{claim.jumlah_item} IMEI · Total klaim <b>{idr(claim.total_klaim)}</b> akan disubmit ke {claim.supplier_nama}.</p>
      <Field label="Tanggal Submit"><DatePicker value={f.tanggal_submit} onChange={(v) => setF({ ...f, tanggal_submit: v })} data-testid="cb-claimdet-submit-tgl" /></Field>
      {lewatDeadline && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 inline-flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Tanggal ini SUDAH LEWAT deadline program ({formatTanggal(claim.deadline_submit_saat_dibuat)}) — batch tetap bisa disubmit, tapi supplier berhak menolak karena lewat batas waktu.
        </div>
      )}
      <Field label="Catatan (opsional)"><textarea rows={2} value={f.catatan} onChange={(e) => setF({ ...f, catatan: e.target.value })} data-testid="cb-claimdet-submit-catatan" className={inputCls + " h-auto py-2"} /></Field>
    </Modal>
  );
}

// ============ Modal: Kelola IMEI (tambah/keluarkan) ============
function KelolaImeiModal({ claim, items, bolehTambah, programTerkunci, onClose, onDone }) {
  const aktif = items.filter((i) => i.klaim_aktif);
  const [hapus, setHapus] = useState(() => new Set());
  const [kandidat, setKandidat] = useState([]);
  const [loadingKandidat, setLoadingKandidat] = useState(bolehTambah);
  const [cari, setCari] = useState("");
  const [tambah, setTambah] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!bolehTambah) return;
    api.get(`${CB_BASE}/programs/${claim.program_id}/kandidat`, { params: { status: "eligible" } })
      .then((r) => setKandidat(r.data.items || []))
      .catch(() => toast.error("Gagal memuat IMEI eligible tambahan"))
      .finally(() => setLoadingKandidat(false));
  }, [claim.program_id, bolehTambah]);

  const kandidatFiltered = useMemo(() => {
    const t = cari.trim().toLowerCase();
    if (!t) return kandidat;
    return kandidat.filter((k) => k.imei.toLowerCase().includes(t) || k.sku.toLowerCase().includes(t) || (k.nama_produk || "").toLowerCase().includes(t));
  }, [kandidat, cari]);

  const toggleHapus = (imei) => setHapus((prev) => { const n = new Set(prev); n.has(imei) ? n.delete(imei) : n.add(imei); return n; });
  const toggleTambah = (imei) => setTambah((prev) => { const n = new Set(prev); n.has(imei) ? n.delete(imei) : n.add(imei); return n; });

  const submit = async () => {
    if (hapus.size === 0 && tambah.size === 0) return onClose();
    setBusy(true);
    try {
      const r = await api.post(`${CB_BASE}/claims/${claim.id}/items`, {
        tambah_imeis: [...tambah], hapus_imeis: [...hapus],
      });
      // Sama seperti BuatClaimModal — IMEI di luar periode klaim TETAP
      // ditambahkan (permintaan bos 21 Agu E1), tapi toast MERAH supaya
      // langsung kelihatan, bukan toast sukses biasa.
      if (r.data.di_luar_periode_klaim) toast.error(r.data.message); else toast.success(r.data.message);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah daftar IMEI"); }
    finally { setBusy(false); }
  };

  return (
    // `dangerous` — dua daftar checkbox dinamis (keluarkan/tambah IMEI); kalau
    // tersentuh tutup tanpa sadar, pilihan yang sudah dicentang hilang semua.
    <Modal title="Kelola IMEI dalam Batch" icon={PlusCircle} onClose={onClose} onSubmit={submit}
           submitLabel="Simpan Perubahan" busy={busy} testid="cb-claimdet-items-modal" wide
           dangerous>
      <div>
        <p className="text-sm font-semibold text-slate-800 mb-2">IMEI dalam batch ini ({aktif.length})</p>
        <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-slate-100">
          {aktif.map((it) => (
            <div key={it.id} className="px-3 py-1.5 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="font-mono">{it.imei}</span><span className="text-slate-500">{it.sku}</span>
                <ItemStatusPill status={it.status} />
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono">{idr(it.cashback_nominal)}</span>
                {["draft", "submitted"].includes(it.status) && (
                  <button onClick={() => toggleHapus(it.imei)} data-testid={`cb-claimdet-items-del-${it.imei}`}
                          className={hapus.has(it.imei) ? "text-rose-700" : "text-rose-400 hover:text-rose-700"} title="Keluarkan dari batch">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        {hapus.size > 0 && <p className="text-[11px] text-rose-600 mt-1">{hapus.size} IMEI akan dikeluarkan dari batch.</p>}
      </div>

      {!bolehTambah ? (
        <p className="pt-2 border-t border-slate-100 text-xs text-slate-500" data-testid="cb-claimdet-items-tambah-tutup">
          Batch ini sudah diputuskan — IMEI baru dimasukkan ke Claim Batch baru, bukan ke sini.
        </p>
      ) : programTerkunci ? (
        // PP4: program Selesai / menunggu PIC — backend menolak tambah IMEI (409).
        <p className="pt-2 border-t border-slate-100 text-xs text-indigo-800" data-testid="cb-claimdet-items-tambah-terkunci">
          {programTerkunci}
        </p>
      ) : (
      <div className="pt-2 border-t border-slate-100">
          <p className="text-sm font-semibold text-slate-800 mb-2">Tambah IMEI Eligible Lainnya</p>
          <div className="relative mb-2">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={cari} onChange={(e) => setCari(e.target.value)} placeholder="Cari IMEI/SKU/produk..." data-testid="cb-claimdet-items-cari"
                   className="w-full h-9 pl-9 pr-3 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-slate-100">
            {loadingKandidat ? (
              <p className="p-3 text-xs text-slate-400">Memuat IMEI eligible...</p>
            ) : kandidatFiltered.length === 0 ? (
              <p className="p-3 text-xs text-slate-400">Tidak ada IMEI eligible tambahan untuk program ini.</p>
            ) : kandidatFiltered.map((k) => (
              <label key={k.imei} className="px-3 py-1.5 flex items-center justify-between text-xs cursor-pointer hover:bg-slate-50">
                <span className="flex items-center gap-2">
                  <input type="checkbox" checked={tambah.has(k.imei)} onChange={() => toggleTambah(k.imei)} data-testid={`cb-claimdet-items-add-${k.imei}`} />
                  <span className="font-mono">{k.imei}</span><span className="text-slate-500">{k.sku}</span>
                </span>
                <span className="font-mono">{idr(k.cashback_nominal)}</span>
              </label>
            ))}
          </div>
          {tambah.size > 0 && <p className="text-[11px] text-emerald-700 mt-1">{tambah.size} IMEI akan ditambahkan ke batch.</p>}
      </div>
      )}
    </Modal>
  );
}

// ============ Modal: Putuskan Approve/Reject (brief butir 14-15, approval PARSIAL) ============
function PutuskanModal({ claim, items, onClose, onDone }) {
  const { can } = useAuth();
  // Impor hasil supplier (A10) memanggil endpoint yang digerbangi
  // `finance_cashback_claim.update` — kunci yang SAMA dengan approve.
  const bolehImpor = can("finance_cashback_claim", "update");
  const [decisions, setDecisions] = useState(() => Object.fromEntries(items.map((it) => [it.id, { mode: null, approved_nominal: it.cashback_nominal, alasan_tolak: "" }])));
  const [busy, setBusy] = useState(false);

  const setDecision = (id, patch) => setDecisions((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  // Usulan dari berkas supplier hanya MENGISI pilihan (bisa diubah staf sebelum
  // disimpan); IMEI yang tidak ada di berkas tidak disentuh. Baris yang DITOLAK
  // menurut kolom Status berkas dipilihkan "Tolak" dengan alasan dari berkas
  // (tinjauan PP r2 — dulu ikut dipilihkan "Setujui").
  const terapkanUsulan = (hasil) => setDecisions((prev) => {
    const n = { ...prev };
    for (const u of (hasil && hasil.usulan) || []) {
      if (n[u.item_id]) n[u.item_id] = { ...n[u.item_id], mode: "approve", approved_nominal: u.approved_nominal };
    }
    for (const t of (hasil && hasil.ditolak_di_berkas) || []) {
      if (n[t.item_id]) n[t.item_id] = { ...n[t.item_id], mode: "reject", alasan_tolak: t.alasan_tolak || "" };
    }
    return n;
  });

  const keputusan = items
    .filter((it) => decisions[it.id]?.mode)
    .map((it) => {
      const d = decisions[it.id];
      return d.mode === "approve"
        ? { imei: it.imei, keputusan: "approve", approved_nominal: Number(d.approved_nominal) }
        : { imei: it.imei, keputusan: "reject", alasan_tolak: d.alasan_tolak.trim() };
    });
  // Batas atas SAMA dengan backend (`approve_claim`: > 0 dan <= klaim + 0,01).
  // Backend kini memvalidasi SEMUA keputusan sebelum menulis satu pun — satu
  // nominal kebesaran menolak seluruh kiriman, jadi layar harus menahannya lebih dulu.
  const klaimPerImei = Object.fromEntries(items.map((it) => [it.imei, Number(it.cashback_nominal) || 0]));
  const nominalSah = (k) => k.approved_nominal > 0 && k.approved_nominal <= (klaimPerImei[k.imei] || 0) + 0.01;
  const valid = keputusan.length > 0 && keputusan.every((k) =>
    k.keputusan === "approve" ? nominalSah(k) : k.alasan_tolak.length > 0);

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const r = await api.post(`${CB_BASE}/claims/${claim.id}/approve`, { keputusan });
      // IMEI yang berubah bersamaan (mis. dikeluarkan dari batch) dilewati
      // backend, bukan ditimpa — toast kuning supaya tidak terbaca "semua tersimpan".
      if ((r.data.dilewati || []).length > 0) toast.warning(r.data.message); else toast.success(r.data.message);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan keputusan"); }
    finally { setBusy(false); }
  };

  return (
    // `dangerous` — daftar IMEI dinamis dengan nominal disetujui yang bisa
    // diketik ulang per baris; keputusan yang belum disimpan gampang hilang
    // kalau modal ini tertutup tak sengaja.
    <Modal title="Putuskan Approve / Reject per IMEI" icon={CheckCircle2} onClose={onClose} onSubmit={submit}
           submitLabel={`Simpan ${keputusan.length} Keputusan`} busy={busy} disabled={!valid} testid="cb-claimdet-approve-modal" wide
           dangerous>
      <p className="text-xs text-slate-500">Boleh diputuskan sebagian — sisanya bisa diputuskan lain kali lewat tombol yang sama.</p>
      {bolehImpor && <ImporHasilSupplier claimId={claim.id} cbBase={CB_BASE} onUsulan={terapkanUsulan} />}
      <div className="space-y-2 max-h-[55vh] overflow-y-auto">
        {items.map((it) => {
          const d = decisions[it.id];
          return (
            <div key={it.id} data-testid={`cb-approve-row-${it.imei}`} className="border border-slate-200 rounded-lg p-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-mono text-xs">{it.imei} <span className="text-slate-500 font-sans">· {it.sku}</span></p>
                  <p className="text-[11px] text-slate-500">Klaim {idr(it.cashback_nominal)}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setDecision(it.id, { mode: "approve" })} data-testid={`cb-approve-btn-approve-${it.imei}`}
                          className={`h-8 px-3 rounded-lg border text-xs font-medium ${d.mode === "approve" ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-200 text-slate-600 hover:bg-emerald-50"}`}>
                    Setujui
                  </button>
                  <button onClick={() => setDecision(it.id, { mode: "reject" })} data-testid={`cb-approve-btn-reject-${it.imei}`}
                          className={`h-8 px-3 rounded-lg border text-xs font-medium ${d.mode === "reject" ? "bg-rose-600 border-rose-600 text-white" : "border-slate-200 text-slate-600 hover:bg-rose-50"}`}>
                    Tolak
                  </button>
                  {d.mode && (
                    <button onClick={() => setDecision(it.id, { mode: null })} data-testid={`cb-approve-btn-nanti-${it.imei}`}
                            className="h-8 px-2 rounded-lg text-xs text-slate-400 hover:text-slate-700">Batal pilih</button>
                  )}
                </div>
              </div>
              {d.mode === "approve" && (
                <div className="mt-2">
                  <label className="block text-[11px] text-slate-500 mb-1">Nominal Disetujui (maks. {idr(it.cashback_nominal)})</label>
                  <InputRupiah value={d.approved_nominal} onChange={(n) => setDecision(it.id, { approved_nominal: n })}
                               testid={`cb-approve-nominal-${it.imei}`} className={inputCls + " max-w-xs"} />
                  {Number(d.approved_nominal) > (Number(it.cashback_nominal) || 0) + 0.01 && (
                    <p className="text-[11px] text-rose-600 mt-1" data-testid={`cb-approve-nominal-lebih-${it.imei}`}>
                      Melebihi cashback yang diklaim — maksimal {idr(it.cashback_nominal)}.
                    </p>
                  )}
                </div>
              )}
              {d.mode === "reject" && (
                <div className="mt-2">
                  <label className="block text-[11px] text-slate-500 mb-1">Alasan Penolakan (wajib)</label>
                  <textarea rows={2} value={d.alasan_tolak} onChange={(e) => setDecision(it.id, { alasan_tolak: e.target.value })}
                            data-testid={`cb-approve-alasan-${it.imei}`} className={inputCls + " h-auto py-1.5"} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

// ============ Modal: Koreksi Keputusan Supplier (tinjauan PP r2) ============
// Supplier mentransfer KURANG dari total disetujui (dipotong biaya transfer, atau
// sebagian IMEI tidak dibayar): nominal disetujui diturunkan / IMEI dijadikan
// ditolak supaya batch bisa final dari uang yang BENAR-BENAR diterima. Tanpa
// jurnal baru — backend (`koreksi_keputusan_claim`) menolak total baru di bawah
// uang yang sudah tercatat diterima; layar menahannya lebih dulu dengan aturan
// yang sama supaya tombol Simpan tidak berujung 400.
function KoreksiKeputusanModal({ claim, items, onClose, onDone }) {
  const [rows, setRows] = useState(() => Object.fromEntries(items.map((it) => [it.id, {
    mode: null, approved_nominal: Number(it.approved_nominal) || 0, alasan_tolak: "" }])));
  const [alasan, setAlasan] = useState("");
  const [busy, setBusy] = useState(false);
  const setRow = (id, patch) => setRows((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const dipilih = items.filter((it) => rows[it.id]?.mode);
  const keputusan = dipilih.map((it) => {
    const d = rows[it.id];
    return d.mode === "approve"
      ? { imei: it.imei, keputusan: "approve", approved_nominal: Number(d.approved_nominal) }
      : { imei: it.imei, keputusan: "reject", alasan_tolak: d.alasan_tolak.trim() || null };
  });
  const lama = (it) => Number(it.approved_nominal) || 0;
  const baru = (it) => (rows[it.id].mode === "reject" ? 0 : Number(rows[it.id].approved_nominal) || 0);
  const totalBaru = round2((claim.total_disetujui || 0) + dipilih.reduce((s, it) => s + baru(it) - lama(it), 0));
  const diterima = Number(claim.nominal_settled) || 0;
  const kurangDariDiterima = totalBaru < diterima - 0.5;
  // Aturan nominal SAMA dengan backend: > 0, <= klaim, dan memang berubah.
  const nominalSah = (it) => rows[it.id].mode !== "approve" || (baru(it) > 0
    && baru(it) <= (Number(it.cashback_nominal) || 0) + 0.01 && Math.abs(baru(it) - lama(it)) >= 0.005);
  const valid = keputusan.length > 0 && alasan.trim().length > 0 && dipilih.every(nominalSah) && !kurangDariDiterima;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const r = await api.post(`${CB_BASE}/claims/${claim.id}/koreksi-keputusan`, { keputusan, alasan: alasan.trim() });
      if ((r.data.dilewati || []).length > 0) toast.warning(r.data.message); else toast.success(r.data.message);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan koreksi"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Koreksi Keputusan Supplier" icon={Pencil} onClose={onClose} onSubmit={submit}
           submitLabel={`Simpan ${keputusan.length} Koreksi`} busy={busy} disabled={!valid} testid="cb-claimdet-koreksi-modal"
           wide dangerous>
      <p className="text-xs text-slate-600">
        Pakai ini kalau supplier membayar <b>kurang</b> dari yang disetujui (mis. dipotong biaya transfer) atau tidak
        membayar sebagian IMEI. Tidak ada jurnal baru — pendapatan yang tercatat tetap uang yang benar-benar diterima.
        Kalau total baru sama dengan uang yang sudah diterima, batch otomatis lunas.
      </p>
      <p className="text-xs text-slate-700" data-testid="cb-koreksi-ringkas">
        Total disetujui {idr(claim.total_disetujui || 0)} → <b>{idr(totalBaru)}</b> · sudah diterima {idr(diterima)}
      </p>
      {kurangDariDiterima && (
        <p className="text-xs text-rose-600" data-testid="cb-koreksi-kurang-dari-diterima">
          Total baru lebih kecil dari uang yang sudah tercatat diterima — void settlement yang berlebih dulu.
        </p>
      )}
      <div className="space-y-2 max-h-[45vh] overflow-y-auto">
        {items.map((it) => {
          const d = rows[it.id];
          return (
            <div key={it.id} data-testid={`cb-koreksi-row-${it.imei}`} className="border border-slate-200 rounded-lg p-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-mono text-xs">{it.imei} <span className="text-slate-500 font-sans">· {it.sku}</span></p>
                  <p className="text-[11px] text-slate-500">Klaim {idr(it.cashback_nominal)} · disetujui {idr(it.approved_nominal)}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setRow(it.id, { mode: "approve" })} data-testid={`cb-koreksi-btn-nominal-${it.imei}`}
                          className={`h-8 px-3 rounded-lg border text-xs font-medium ${d.mode === "approve" ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-200 text-slate-600 hover:bg-emerald-50"}`}>
                    Ubah nominal
                  </button>
                  <button onClick={() => setRow(it.id, { mode: "reject" })} data-testid={`cb-koreksi-btn-tolak-${it.imei}`}
                          className={`h-8 px-3 rounded-lg border text-xs font-medium ${d.mode === "reject" ? "bg-rose-600 border-rose-600 text-white" : "border-slate-200 text-slate-600 hover:bg-rose-50"}`}>
                    Jadi ditolak
                  </button>
                  {d.mode && (
                    <button onClick={() => setRow(it.id, { mode: null })} data-testid={`cb-koreksi-btn-batal-${it.imei}`}
                            className="h-8 px-2 rounded-lg text-xs text-slate-400 hover:text-slate-700">Batal pilih</button>
                  )}
                </div>
              </div>
              {d.mode === "approve" && (
                <div className="mt-2">
                  <label className="block text-[11px] text-slate-500 mb-1">Nominal disetujui baru (maks. {idr(it.cashback_nominal)})</label>
                  <InputRupiah value={d.approved_nominal} onChange={(n) => setRow(it.id, { approved_nominal: n })}
                               testid={`cb-koreksi-nominal-${it.imei}`} className={inputCls + " max-w-xs"} />
                </div>
              )}
              {d.mode === "reject" && (
                <div className="mt-2">
                  <label className="block text-[11px] text-slate-500 mb-1">Alasan penolakan (kosong = alasan koreksi di bawah)</label>
                  <textarea rows={2} value={d.alasan_tolak} onChange={(e) => setRow(it.id, { alasan_tolak: e.target.value })}
                            data-testid={`cb-koreksi-alasan-tolak-${it.imei}`} className={inputCls + " h-auto py-1.5"} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Field label="Alasan koreksi (wajib)">
        <textarea rows={2} value={alasan} onChange={(e) => setAlasan(e.target.value)} data-testid="cb-koreksi-alasan"
                  placeholder="mis. supplier memotong biaya transfer Rp 1.000" className={inputCls + " h-auto py-1.5"} />
      </Field>
    </Modal>
  );
}

// Kategori yang dipakai backend kalau `kategori_cn` kosong — SAMA dengan
// `KATEGORI_CN_DEFAULT` di `cashback_claim.py`.
const KATEGORI_CN_DEFAULT = "Price Protection";

// ============ Modal: Settlement (brief butir 17-18, 3 metode) ============
function SettlementModal({ claim, outstanding, kategoriBawaan, onClose, onDone }) {
  const { can } = useAuth();
  // Metode Credit Note MENERBITKAN lalu MENYERAHKAN CN atas nama pengguna ini,
  // jadi backend (`settlement_claim`) menegakkan izin Tambah DAN Ubah Credit
  // Note di atas izin Ubah Cashback Claim. Tanpa gerbang yang sama di sini,
  // staf yang hanya memegang izin cashback melihat opsi yang berujung 403.
  const bolehSettleCN = can("finance_credit_note", "create") && can("finance_credit_note", "update");
  const [metode, setMetode] = useState("");
  // `pph` = PPh yang DIPOTONG supplier saat mentransfer (jawaban Bos no. 35,
  // 19 Okt 2026). `nominal` tetap pendapatan yang diakui; yang masuk rekening
  // = nominal − pph. Hanya untuk kas_bank — dua metode lain tidak memindahkan
  // uang, jadi tidak ada yang bisa dipotong (backend menolak 400).
  const [f, setF] = useState({ tanggal: hariIniISO(), nominal: outstanding, keterangan: "", pph: 0 });
  const [kasBank, setKasBank] = useState([]);
  const [akunId, setAkunId] = useState("");
  const [kategoriOpts, setKategoriOpts] = useState([]);
  const [kategoriCn, setKategoriCn] = useState("");
  const [utangList, setUtangList] = useState([]);
  const [loadingUtang, setLoadingUtang] = useState(false);
  const [searchUtang, setSearchUtang] = useState(claim.supplier_nama);
  const [utangSelected, setUtangSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (metode === "kas_bank" && kasBank.length === 0) {
      api.get("/finance/accounts/kas-bank").then((r) => setKasBank(r.data || [])).catch(() => {});
    }
    if (metode === "credit_note" && kategoriOpts.length === 0) {
      api.get("/credit-note/kategori-options").then((r) => {
        const opts = r.data.kategori || [];
        setKategoriOpts(opts);
        // Bawaan dari backend (PP4: "Support Aktivasi" untuk batch program
        // aktivasi) — dipakai hanya kalau memang ada di daftar kategori aktif.
        const pilihan = [kategoriBawaan, KATEGORI_CN_DEFAULT].find((k) => k && opts.includes(k));
        setKategoriCn(pilihan || opts[0] || "");
      }).catch(() => {
        // Daftar kategori butuh izin LIHAT Credit Note, sedangkan settlement CN
        // butuh Tambah+Ubah — pemegang keduanya tanpa izin lihat tetap boleh
        // menyimpan. Jatuh ke kategori bawaan backend, bukan form yang terkunci.
        // `kategoriBawaan` hanya dikirim backend kalau kategorinya aktif.
        const k = kategoriBawaan || KATEGORI_CN_DEFAULT;
        setKategoriOpts([k]);
        setKategoriCn(k);
      });
    }
    if (metode === "potong_utang_langsung" && utangList.length === 0) {
      setLoadingUtang(true);
      api.get("/finance/utang", { params: { status: "aktif" } })
        .then((r) => setUtangList(r.data.items || []))
        .catch(() => toast.error("Gagal memuat daftar utang aktif"))
        .finally(() => setLoadingUtang(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metode]);

  const utangFiltered = useMemo(() => {
    const term = searchUtang.trim().toLowerCase();
    return utangList
      .filter((u) => ((u.nominal || 0) - (u.nominal_terbayar || 0)) > 0.5)
      .filter((u) => !term || (u.supplier_nama || "").toLowerCase().includes(term) || (u.code || "").toLowerCase().includes(term))
      .slice(0, 30);
  }, [utangList, searchUtang]);

  // Cermin pra-validasi backend, supaya tombol Simpan tidak berujung 400
  // (aturan 3 CLAUDE.md): PPh hanya untuk kas_bank, tidak negatif, dan harus
  // lebih kecil dari nominal — kalau tidak ada uang yang masuk, itu bukan
  // settlement.
  const pphNilai = metode === "kas_bank" ? Number(f.pph) || 0 : 0;
  const pphSah = pphNilai >= 0 && (pphNilai === 0 || pphNilai < Number(f.nominal));
  const valid = !!metode && Number(f.nominal) > 0 && Number(f.nominal) <= outstanding + 0.5 &&
    (metode !== "kas_bank" || !!akunId) &&
    (metode !== "potong_utang_langsung" || !!utangSelected) &&
    (metode !== "credit_note" || !!kategoriCn) && pphSah;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const body = { metode, nominal: Number(f.nominal), tanggal: f.tanggal, keterangan: f.keterangan };
      if (metode === "kas_bank") {
        body.akun_id = akunId;
        if (pphNilai > 0) body.pph_nominal = pphNilai;
      }
      if (metode === "potong_utang_langsung") body.utang_id = utangSelected.id;
      if (metode === "credit_note") body.kategori_cn = kategoriCn;
      const r = await api.post(`${CB_BASE}/claims/${claim.id}/settlement`, body);
      toast.success(r.data.message); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencatat settlement"); }
    finally { setBusy(false); }
  };

  return (
    // `dangerous` — nominal settlement diketik/dipilih staf sendiri (InputRupiah),
    // plus pilihan metode & akun/utang yang gampang hilang kalau tertutup tak sengaja.
    <Modal title="Catat Settlement" icon={Wallet} onClose={onClose} onSubmit={submit} submitLabel="Simpan Settlement"
           busy={busy} disabled={!valid} testid="cb-claimdet-settlement-modal" wide
           dangerous>
      <p className="text-sm text-slate-600">Outstanding batch ini: <b>{idr(outstanding)}</b></p>
      <Field label="Metode Settlement">
        <select value={metode} onChange={(e) => setMetode(e.target.value)} data-testid="cb-settle-metode" className={inputCls}>
          <option value="">Pilih metode...</option>
          {bolehSettleCN && <option value="credit_note" data-testid="cb-settle-metode-opt-cn">Credit Note (CN)</option>}
          <option value="kas_bank">Kas/Bank Langsung</option>
          <option value="potong_utang_langsung">Potong Utang Langsung</option>
        </select>
      </Field>

      {metode === "credit_note" && (
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800">
          Credit Note akan diterbitkan OTOMATIS untuk supplier ini dan langsung diserahkan — bisa ditelusuri lengkap di menu <b>Finance &gt; Credit Note</b> (aging, pencairan, pemakaian).
        </div>
      )}
      {metode === "kas_bank" && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700">
          Uang diterima LANGSUNG dari supplier tanpa dokumen CN formal (mis. transfer langsung) — dijurnal ke akun kas/bank yang dipilih, lawannya Pendapatan Rebate Supplier (4910).
          {/* Jawaban Bos no. 35: dua sebab "transfer masuk lebih kecil" ditangani
              BERBEDA, dan staf harus tahu yang mana sebelum mengisi. */}
          <span className="block mt-1.5 text-slate-600" data-testid="cb-settle-kasbank-selisih">
            Kalau uang masuk <b>lebih kecil</b> dari nominal: isi <b>PPh dipotong</b> di bawah bila
            potongannya pajak (pendapatan tetap penuh, potongannya jadi kredit pajak). Kalau sebabnya
            <b> biaya transfer</b> atau supplier menyetujui lebih sedikit, jangan pakai medan itu —
            turunkan nominal disetujui lewat tombol <b>Koreksi Keputusan</b>.
          </span>
        </div>
      )}
      {metode === "potong_utang_langsung" && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700">
          Memotong TAGIHAN utang supplier yang sama tanpa uang keluar (lawannya Pendapatan Rebate Supplier 4910) — hanya bisa memilih utang milik supplier <b>{claim.supplier_nama}</b>.
        </div>
      )}

      {metode && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tanggal"><DatePicker value={f.tanggal} onChange={(v) => setF({ ...f, tanggal: v })} data-testid="cb-settle-tgl" /></Field>
          <Field label="Nominal" hint={`Maks. ${idr(outstanding)}`}>
            <InputRupiah value={f.nominal} onChange={(n) => setF({ ...f, nominal: n })} testid="cb-settle-nominal" className={inputCls} />
          </Field>
        </div>
      )}

      {metode === "credit_note" && (
        <Field label="Kategori CN">
          <select value={kategoriCn} onChange={(e) => setKategoriCn(e.target.value)} data-testid="cb-settle-kategori-cn" className={inputCls}>
            {kategoriOpts.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </Field>
      )}

      {metode === "kas_bank" && (
        <Field label="PPh dipotong supplier (opsional)"
               hint="Kosongkan kalau supplier mentransfer penuh">
          <InputRupiah value={f.pph} onChange={(n) => setF({ ...f, pph: n })} testid="cb-settle-pph"
                       className={inputCls} />
          {pphNilai > 0 && pphSah && (
            <p className="text-[11px] text-slate-600 mt-1" data-testid="cb-settle-pph-rincian">
              Masuk rekening <b>{idr(Number(f.nominal) - pphNilai)}</b> · Pendapatan 4910 tetap
              <b> {idr(Number(f.nominal))}</b> · PPh <b>{idr(pphNilai)}</b> dicatat sebagai kredit pajak.
            </p>
          )}
          {pphNilai > 0 && !pphSah && (
            <p className="text-[11px] text-rose-600 mt-1" data-testid="cb-settle-pph-salah">
              PPh harus lebih kecil dari nominal settlement — kalau supplier tidak mentransfer apa pun,
              ini bukan settlement.
            </p>
          )}
        </Field>
      )}

      {metode === "kas_bank" && (
        <Field label="Masuk ke Akun">
          <select value={akunId} onChange={(e) => setAkunId(e.target.value)} data-testid="cb-settle-akun" className={inputCls}>
            <option value="">Pilih...</option>
            {kasBank.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>
      )}

      {metode === "potong_utang_langsung" && (
        <div>
          <Field label="Cari Utang Aktif (nama supplier / kode)">
            <input value={searchUtang} onChange={(e) => setSearchUtang(e.target.value)} data-testid="cb-settle-utang-search" className={inputCls} />
          </Field>
          <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-slate-100 mt-1.5">
            {loadingUtang ? (
              <p className="p-3 text-xs text-slate-400">Memuat utang aktif...</p>
            ) : utangFiltered.length === 0 ? (
              <p className="p-3 text-xs text-slate-400">Tidak ada utang aktif yang cocok.</p>
            ) : utangFiltered.map((u) => {
              const sisa = (u.nominal || 0) - (u.nominal_terbayar || 0);
              return (
                <button type="button" key={u.id} onClick={() => setUtangSelected(u)} data-testid={`cb-settle-utang-${u.id}`}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${utangSelected?.id === u.id ? "bg-emerald-50" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-slate-900 truncate">{u.supplier_nama}</span>
                    <span className="font-mono text-xs text-slate-700 shrink-0">{idr(sisa)}</span>
                  </div>
                  <p className="text-[11px] text-slate-500">{u.code}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {metode && <Field label="Keterangan (opsional)"><input value={f.keterangan} onChange={(e) => setF({ ...f, keterangan: e.target.value })} data-testid="cb-settle-ket" className={inputCls} /></Field>}
    </Modal>
  );
}

// ============ Modal: Reclaim IMEI Ditolak (brief butir 16) ============
function ReclaimModal({ claim, items, onClose, onDone }) {
  const [selected, setSelected] = useState(() => new Set(items.map((i) => i.imei)));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (imei) => setSelected((prev) => { const n = new Set(prev); n.has(imei) ? n.delete(imei) : n.add(imei); return n; });
  const valid = selected.size > 0 && reason.trim().length > 0;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const r = await api.post(`${CB_BASE}/claims/${claim.id}/reclaim`, { reason: reason.trim(), imeis: [...selected] });
      toast.success(r.data.message); onDone(r.data.item);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal reclaim IMEI"); }
    finally { setBusy(false); }
  };

  return (
    // `dangerous` — daftar checkbox dinamis (banyak IMEI bisa dicentang/dilepas)
    // plus alasan reclaim yang wajib diisi; bukan sekadar satu baris alasan.
    <Modal title="Reclaim IMEI yang Ditolak" icon={RefreshCcw} onClose={onClose} onSubmit={submit}
           submitLabel={`Buat Batch Baru (${selected.size} IMEI)`} busy={busy} disabled={!valid} testid="cb-claimdet-reclaim-modal" wide
           dangerous>
      <p className="text-xs text-slate-500">IMEI terpilih dipindahkan ke Claim Batch BARU. Item lama di batch ini tetap tersimpan sebagai jejak audit (statusnya tetap "Ditolak").</p>
      <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-slate-100">
        {items.map((it) => (
          <label key={it.id} className="px-3 py-2 flex items-center justify-between text-sm cursor-pointer hover:bg-slate-50">
            <span className="flex items-center gap-2">
              <input type="checkbox" checked={selected.has(it.imei)} onChange={() => toggle(it.imei)} data-testid={`cb-reclaim-cb-${it.imei}`} />
              <span className="font-mono text-xs">{it.imei}</span><span className="text-slate-500 text-xs">{it.sku}</span>
            </span>
            <span className="text-right">
              <span className="font-mono text-xs block">{idr(it.cashback_nominal)}</span>
              <span className="text-[10px] text-rose-600">{it.rejection_reason}</span>
            </span>
          </label>
        ))}
      </div>
      <Field label="Alasan Reclaim (wajib)">
        <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cb-reclaim-alasan" className={inputCls + " h-auto py-2"} />
      </Field>
    </Modal>
  );
}
